import type { ProductItemIntegrationResponse } from "apiClients/base-generated";

/** Matches `Table` `rowKey` for integration product item rows. */
export function integrationProductRowKey(record: ProductItemIntegrationResponse, index?: number): string {
  const k = record.productItemId ?? record.productId ?? record.id;
  return k != null ? String(k) : `__row_${index ?? 0}`;
}

/** Maps selected row keys to API `integrationSyncIds` (`ProductItemIntegrationResponse.id`). */
export function resolveIntegrationSyncIdsFromRowKeys(
  rowKeys: string[],
  rows: ProductItemIntegrationResponse[],
): number[] {
  const wanted = new Set(rowKeys);
  const ids: number[] = [];
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    if (r.id == null) continue;
    if (wanted.has(integrationProductRowKey(r, i))) {
      ids.push(r.id);
    }
  }
  return ids;
}

/**
 * Maps row keys to API `productItemIds` (`productItemId`, sedan `productId` / `id`).
 * Returnerar `null` om någon nyckel saknar matchande rad eller saknar ID.
 */
export function resolveProductIdsFromRowKeys(
  rowKeys: string[],
  rows: ProductItemIntegrationResponse[],
): number[] | null {
  const set = new Set<number>();
  for (const key of rowKeys) {
    let found: ProductItemIntegrationResponse | undefined;
    for (let i = 0; i < rows.length; i++) {
      if (integrationProductRowKey(rows[i], i) === key) {
        found = rows[i];
        break;
      }
    }
    if (!found) return null;
    const pid = found.productItemId ?? found.productId ?? found.id;
    if (pid == null) return null;
    set.add(pid);
  }
  return [...set];
}

/** All table rows whose `rowKey` is in `rowKeys` (preserves table order). */
export function resolveRecordsFromRowKeys(
  rowKeys: string[],
  rows: ProductItemIntegrationResponse[],
): ProductItemIntegrationResponse[] {
  const wanted = new Set(rowKeys);
  const out: ProductItemIntegrationResponse[] = [];
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    if (wanted.has(integrationProductRowKey(r, i))) {
      out.push(r);
    }
  }
  return out;
}
